// ApplicantTracker — full applicant pipeline management dashboard
// Sections: Pipeline Kanban, Disengaged Applicants, AIS Tracker, Time-in-Process Chart, Coordinator Workload, Full DataTable

import { useMemo, useState } from "react";
import { usePolling, useAggregates } from "../hooks/useDashboard";
import { useApplicantComments } from "../hooks/useForge";
import type { ApplicantCommentRow } from "../hooks/useForge";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import KanbanBoard from "../components/dashboard/KanbanBoard";
import type {
  KanbanColumn,
  KanbanCard,
} from "../components/dashboard/KanbanBoard";
import AlertList from "../components/dashboard/AlertList";
import type { AlertItem } from "../components/dashboard/AlertList";
import StatusBadge from "../components/dashboard/StatusBadge";
import { useTheme } from "../hooks/useTheme";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

// ── Applicant data types ──────────────────────────────────────────────────────

export interface ApplicantRow {
  applicant_name_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  pipeline_stage?: string | null;
  days_since_applied?: number | string | null;
  ais_status?: string | null;
  interest?: string | null;
  application_result?: string | null;
  how_did_you_hear_about_us?: string | null;
  screening_one_check_completed?: string | null;
  kpi_time_in_process?: number | string | null;
  kpi_days_since_applied_category?: string | null;
  created?: string | null;
  internalcreateddate?: string | null;
  bod_vote?: string | null;
  [key: string]: unknown;
}

interface ApplicantsResponse {
  applicants: ApplicantRow[];
  count: number;
}

// ── Date range ────────────────────────────────────────────────────────────────

type DateRange = "day" | "week" | "month" | "quarter" | "year" | "all";

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  day: "Today",
  week: "This Week",
  month: "This Month",
  quarter: "This Quarter",
  year: "This Year",
  all: "All Time",
};

function getDateRangeCutoff(range: DateRange): Date | null {
  const now = new Date();
  switch (range) {
    case "day": {
      const d = new Date(now);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    case "week": {
      const d = new Date(now);
      const day = d.getDay();
      d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      d.setHours(0, 0, 0, 0);
      return d;
    }
    case "month": {
      return new Date(now.getFullYear(), now.getMonth(), 1);
    }
    case "quarter": {
      const q = Math.floor(now.getMonth() / 3);
      return new Date(now.getFullYear(), q * 3, 1);
    }
    case "year": {
      return new Date(now.getFullYear(), 0, 1);
    }
    case "all":
    default:
      return null;
  }
}

/** DateRangeToggle — pill-style button group */
function DateRangeToggle({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (v: DateRange) => void;
}) {
  const ranges: DateRange[] = [
    "day",
    "week",
    "month",
    "quarter",
    "year",
    "all",
  ];
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {ranges.map((r) => (
        <button
          key={r}
          onClick={() => onChange(r)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors min-h-[32px] ${
            value === r
              ? "bg-dcvfd-accent text-white"
              : "bg-theme-overlay text-theme-secondary hover:bg-theme-overlay/80"
          }`}
        >
          {DATE_RANGE_LABELS[r]}
        </button>
      ))}
    </div>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────

const PIPELINE_STAGES_ORDERED = [
  "2 - New/Contacted",
  "3 - Background Pending",
  "4 - Awaiting AIS",
  "5 - OEMS Processing",
  "6 - Physical Pending",
  "7 - Ready for BOD",
];

/** Normalize stage names for display in column headers */
function stageShortName(stage: string): string {
  const map: Record<string, string> = {
    "2 - New/Contacted": "New / Contacted",
    "3 - Background Pending": "Background",
    "4 - Awaiting AIS": "Awaiting AIS",
    "5 - OEMS Processing": "OEMS",
    "6 - Physical Pending": "Physical",
    "7 - Ready for BOD": "Ready for BOD",
  };
  return map[stage] ?? stage;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function asString(v: unknown, fallback = ""): string {
  if (typeof v === "string") return v;
  return fallback;
}

function applicantDisplayName(row: ApplicantRow): string {
  // Privacy convention (board audit 2026-04-26): show first name + last initial
  // only. Full surnames are not surfaced in the dashboard — board members vote
  // by full name through the BOD-vote workflow itself, not this tracker.
  const first = asString(row.first_name).trim();
  const last = asString(row.last_name).trim();
  if (first && last) return `${first} ${last.charAt(0).toUpperCase()}.`;
  if (first) return first;
  if (last) return `${last.charAt(0).toUpperCase()}.`;
  return asString(row.applicant_name_id) || "Unknown";
}

function applicantId(row: ApplicantRow, index: number): string {
  return asString(row.applicant_name_id) || `applicant-${index}`;
}

/** Derive blockedBy reason based on pipeline stage and field values */
function deriveBlockedBy(row: ApplicantRow): string | undefined {
  const stage = asString(row.pipeline_stage);
  if (stage.includes("Background") && !row.screening_one_check_completed) {
    return "Awaiting background check";
  }
  return undefined;
}

/**
 * Get the applicant's "created" date as a Date object.
 * Checks both `created` and `internalcreateddate` (normalized from InternalCreatedDate).
 */
function applicantCreatedDate(row: ApplicantRow): Date | null {
  const raw = asString(row.created) || asString(row.internalcreateddate);
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Refresh indicator ─────────────────────────────────────────────────────────

function RefreshDot({ lastUpdatedAt }: { lastUpdatedAt: Date | null }) {
  if (!lastUpdatedAt) return null;
  const sec = Math.round((Date.now() - lastUpdatedAt.getTime()) / 1000);
  return (
    <div className="flex items-center gap-1.5 text-xs text-theme-muted">
      <span className="w-1.5 h-1.5 rounded-full bg-status-green shrink-0" />
      <span>Updated {sec < 5 ? "just now" : `${sec}s ago`}</span>
    </div>
  );
}

// ── useApplicants hook ────────────────────────────────────────────────────────

function useApplicants(intervalMs = 60_000) {
  return usePolling<ApplicantsResponse>(
    "/api/dashboard/applicants?limit=5000",
    intervalMs,
  );
}

// ── Time-in-Process Chart ─────────────────────────────────────────────────────

interface StageAvgDays {
  stage: string;
  avgDays: number;
  count: number;
}

interface StageTooltipPayload {
  name: string;
  value: number;
  payload: StageAvgDays;
}

function StageTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: StageTooltipPayload[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0];
  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[160px]">
      <p className="font-semibold text-theme-primary mb-1.5">{label}</p>
      <div className="flex justify-between gap-4">
        <span className="text-theme-muted">Avg days</span>
        <span className="font-bold text-theme-primary">
          {Math.round(item.value)}d
        </span>
      </div>
      <div className="flex justify-between gap-4 mt-0.5">
        <span className="text-theme-muted">Applicants</span>
        <span className="font-semibold text-theme-secondary">
          {item.payload.count}
        </span>
      </div>
    </div>
  );
}

function TimeInProcessChart({
  data,
  isLoading,
}: {
  data: StageAvgDays[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return (
      <div className="flex items-end gap-3 h-52 px-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex-1 flex flex-col gap-1 items-center">
            <div
              className="skeleton w-full rounded-t-sm"
              style={{ height: `${30 + ((i * 19 + 17) % 70)}%` }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-52 gap-2">
        <p className="text-sm text-theme-muted">
          No time-in-process data available
        </p>
        <p className="text-xs text-theme-muted opacity-60">
          Applicant data will appear here after sync
        </p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart
        data={data}
        margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
      >
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="stage"
          tick={{ fontSize: 10, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          angle={-30}
          textAnchor="end"
          height={60}
        />
        <YAxis
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={36}
          label={{
            value: "Avg days",
            angle: -90,
            position: "insideLeft",
            offset: 10,
            style: { fontSize: 10, fill: axisColor },
          }}
        />
        <Tooltip
          content={<StageTooltip />}
          cursor={{
            fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
          }}
        />
        <Bar
          dataKey="avgDays"
          name="Avg Days"
          radius={[4, 4, 0, 0]}
          maxBarSize={56}
        >
          {data.map((entry, index) => {
            const color =
              entry.avgDays > 30
                ? "#dc2626"
                : entry.avgDays >= 14
                  ? "#f59e0b"
                  : "#16a34a";
            return (
              <Cell key={`cell-${index}`} fill={color} fillOpacity={0.85} />
            );
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── AIS Tracker table ─────────────────────────────────────────────────────────

function AisTable({
  rows,
  emptyMsg,
  highlightNoShow = false,
}: {
  rows: ApplicantRow[];
  emptyMsg: string;
  highlightNoShow?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <div className="py-8 text-center flex flex-col items-center gap-1.5">
        {highlightNoShow ? (
          <>
            <span className="text-lg">✓</span>
            <p className="text-sm font-medium text-status-green">{emptyMsg}</p>
          </>
        ) : (
          <p className="text-sm text-theme-muted">{emptyMsg}</p>
        )}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[400px] text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className="px-4 py-2.5 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Name
            </th>
            <th className="px-4 py-2.5 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              AIS Status
            </th>
            <th className="px-4 py-2.5 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Stage
            </th>
            <th className="px-4 py-2.5 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Days Applied
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const isNoShow = asString(row.ais_status)
              .toLowerCase()
              .includes("no show");
            return (
              <tr
                key={applicantId(row, idx)}
                className={`border-b border-theme-subtle hover:bg-theme-overlay transition-colors ${
                  isNoShow
                    ? "bg-red-500/5 border-l-2 border-l-status-red"
                    : idx % 2 !== 0
                      ? "bg-theme-overlay/40"
                      : ""
                }`}
              >
                <td className="px-4 py-2.5 text-sm font-semibold text-theme-primary">
                  {applicantDisplayName(row)}
                </td>
                <td className="px-4 py-2.5 text-sm">
                  <span
                    className={
                      isNoShow
                        ? "font-semibold text-status-red"
                        : "text-theme-secondary"
                    }
                  >
                    {asString(row.ais_status) || "—"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs text-theme-muted">
                  {asString(row.pipeline_stage) || "—"}
                </td>
                <td className="px-4 py-2.5 text-sm text-right tabular-nums text-theme-secondary">
                  {asNumber(row.days_since_applied) > 0
                    ? `${asNumber(row.days_since_applied)}d`
                    : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Coordinator workload table ────────────────────────────────────────────────

interface AuthorWeekCount {
  author: string;
  weekOf: string;
  count: number;
}

/** ISO week label — "Week of Apr 7" style */
function weekLabel(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "Unknown";
  // Get Monday of that week
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return monday.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function buildCoordinatorWorkload(
  comments: ApplicantCommentRow[],
): AuthorWeekCount[] {
  const map = new Map<string, number>();
  for (const c of comments) {
    const author = asString(
      c.author_name || c.author_id || c.author_email,
    ).trim();
    if (!author) continue;
    const dateStr = asString(c.internal_created_date || c.synced_at);
    const week = weekLabel(dateStr);
    const key = `${author}__${week}`;
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([key, count]) => {
      const [author, weekOf] = key.split("__");
      return { author, weekOf, count };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);
}

function CoordinatorTable({
  rows,
  isLoading,
}: {
  rows: AuthorWeekCount[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton h-8 rounded-md" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="py-6 text-center">
        <p className="text-sm text-theme-muted">
          No coordinator activity data available
        </p>
        <p className="text-xs text-theme-muted opacity-60">
          Future enhancement: requires author data in comment records
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[360px] text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className="px-4 py-2.5 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Author
            </th>
            <th className="px-4 py-2.5 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Week of
            </th>
            <th className="px-4 py-2.5 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Comments
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.author}-${row.weekOf}`}
              className="hover:bg-theme-overlay/50 transition-colors"
            >
              <td className="px-4 py-2.5 text-sm font-semibold text-theme-primary">
                {row.author}
              </td>
              <td className="px-4 py-2.5 text-sm text-theme-secondary">
                {row.weekOf}
              </td>
              <td className="px-4 py-2.5 text-sm text-right tabular-nums font-semibold text-theme-primary">
                {row.count}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── StageBadge — color pill for pipeline stage ────────────────────────────────

function StageBadge({ stage }: { stage: string }) {
  const s = stage.toLowerCase();
  let cls = "bg-theme-overlay text-theme-secondary";
  if (s.includes("new") || s.includes("contact"))
    cls = "bg-blue-500/15 text-blue-600 dark:text-blue-400";
  else if (s.includes("background"))
    cls = "bg-amber-500/15 text-amber-700 dark:text-amber-400";
  else if (s.includes("ais"))
    cls = "bg-purple-500/15 text-purple-700 dark:text-purple-400";
  else if (s.includes("oems"))
    cls = "bg-orange-500/15 text-orange-700 dark:text-orange-400";
  else if (s.includes("physical"))
    cls = "bg-indigo-500/15 text-indigo-700 dark:text-indigo-400";
  else if (s.includes("bod") || s.includes("ready"))
    cls = "bg-green-500/15 text-status-green";
  else if (s.includes("complete") || s.includes("8 -"))
    cls = "bg-green-500/20 text-status-green font-bold";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${cls}`}
    >
      {stageShortName(stage) || stage}
    </span>
  );
}

// ── ResultBadge ───────────────────────────────────────────────────────────────

function ResultBadge({ result }: { result: string }) {
  if (!result) return <span className="text-theme-muted text-xs">—</span>;
  const r = result.toLowerCase();
  let cls = "bg-theme-overlay text-theme-secondary";
  if (r.includes("accept") || r.includes("pass") || r.includes("approved"))
    cls = "bg-green-500/15 text-status-green";
  else if (
    r.includes("deny") ||
    r.includes("fail") ||
    r.includes("reject") ||
    r.includes("withdraw")
  )
    cls = "bg-red-500/15 text-status-red";
  else if (r.includes("pending") || r.includes("review"))
    cls = "bg-amber-500/15 text-status-amber";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${cls}`}
    >
      {result}
    </span>
  );
}

// ── Full Applicant DataTable ──────────────────────────────────────────────────

type SortField =
  | "name"
  | "stage"
  | "days"
  | "interest"
  | "source"
  | "result"
  | "created";
type SortDir = "asc" | "desc";

function SortIcon({
  field,
  sortField,
  sortDir,
}: {
  field: SortField;
  sortField: SortField;
  sortDir: SortDir;
}) {
  if (field !== sortField) {
    return (
      <svg
        className="inline ml-1 opacity-30"
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    );
  }
  return sortDir === "asc" ? (
    <svg
      className="inline ml-1 opacity-80 text-brand-adaptive"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ) : (
    <svg
      className="inline ml-1 opacity-80 text-brand-adaptive"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ApplicantDataTable({
  rows,
  isLoading,
}: {
  rows: ApplicantRow[];
  isLoading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [sortField, setSortField] = useState<SortField>("days");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  // Unique stages for filter dropdown
  const stages = useMemo(() => {
    const seen = new Set<string>();
    for (const row of rows) {
      const s = asString(row.pipeline_stage);
      if (s) seen.add(s);
    }
    return Array.from(seen).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    let result = rows;
    if (stageFilter !== "all") {
      result = result.filter((r) => asString(r.pipeline_stage) === stageFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          applicantDisplayName(r).toLowerCase().includes(q) ||
          asString(r.pipeline_stage).toLowerCase().includes(q) ||
          asString(r.interest).toLowerCase().includes(q),
      );
    }
    return result;
  }, [rows, stageFilter, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va: string | number = "";
      let vb: string | number = "";
      switch (sortField) {
        case "name":
          va = applicantDisplayName(a);
          vb = applicantDisplayName(b);
          break;
        case "stage":
          va = asString(a.pipeline_stage);
          vb = asString(b.pipeline_stage);
          break;
        case "days":
          va = asNumber(a.days_since_applied);
          vb = asNumber(b.days_since_applied);
          break;
        case "interest":
          va = asString(a.interest);
          vb = asString(b.interest);
          break;
        case "source":
          va = asString(a.how_did_you_hear_about_us);
          vb = asString(b.how_did_you_hear_about_us);
          break;
        case "result":
          va = asString(a.application_result);
          vb = asString(b.application_result);
          break;
        case "created":
          va = asString(a.created) || asString(a.internalcreateddate);
          vb = asString(b.created) || asString(b.internalcreateddate);
          break;
      }
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [filtered, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(0);
  }

  const thClass =
    "px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider cursor-pointer select-none hover:text-theme-primary transition-colors whitespace-nowrap";

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        <div className="flex gap-3 mb-4">
          <div className="skeleton h-9 w-64 rounded-lg" />
          <div className="skeleton h-9 w-40 rounded-lg" />
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skeleton h-10 rounded-lg" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="py-12 text-center flex flex-col items-center gap-2">
        <p className="text-sm text-theme-muted">
          No applicant records available
        </p>
        <p className="text-xs text-theme-muted opacity-60">
          Data will appear here after the next sync
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Filters bar */}
      <div className="flex items-center gap-3 px-4 pt-4 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-muted"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Search by name or stage…"
            className="w-full pl-8 pr-3 py-2 text-sm bg-theme-overlay border border-theme-subtle rounded-lg text-theme-primary placeholder:text-theme-muted focus:outline-none focus:ring-2 focus:ring-dcvfd-accent/40"
          />
        </div>

        {/* Stage filter */}
        <select
          value={stageFilter}
          onChange={(e) => {
            setStageFilter(e.target.value);
            setPage(0);
          }}
          className="px-3 py-2 text-sm bg-theme-overlay border border-theme-subtle rounded-lg text-theme-primary focus:outline-none focus:ring-2 focus:ring-dcvfd-accent/40"
        >
          <option value="all">All Stages</option>
          {stages.map((s) => (
            <option key={s} value={s}>
              {stageShortName(s)}
            </option>
          ))}
        </select>

        <span className="ml-auto text-xs text-theme-muted tabular-nums shrink-0">
          {filtered.length.toLocaleString()} of {rows.length.toLocaleString()}{" "}
          applicants
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] text-left">
          <thead>
            <tr className="border-b border-theme-subtle">
              <th className={thClass} onClick={() => toggleSort("name")}>
                Name{" "}
                <SortIcon
                  field="name"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => toggleSort("stage")}>
                Stage{" "}
                <SortIcon
                  field="stage"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th
                className={`${thClass} text-right`}
                onClick={() => toggleSort("days")}
              >
                Days{" "}
                <SortIcon
                  field="days"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => toggleSort("interest")}>
                Interest{" "}
                <SortIcon
                  field="interest"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => toggleSort("source")}>
                Source{" "}
                <SortIcon
                  field="source"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => toggleSort("result")}>
                Result{" "}
                <SortIcon
                  field="result"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => toggleSort("created")}>
                Applied{" "}
                <SortIcon
                  field="created"
                  sortField={sortField}
                  sortDir={sortDir}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-10 text-center text-sm text-theme-muted"
                >
                  No applicants match your filters
                </td>
              </tr>
            ) : (
              pageRows.map((row, idx) => {
                const days = asNumber(row.days_since_applied);
                const daysColor =
                  days > 30
                    ? "text-status-red"
                    : days >= 14
                      ? "text-status-amber"
                      : days > 0
                        ? "text-status-green"
                        : "text-theme-muted";
                const createdRaw =
                  asString(row.created) || asString(row.internalcreateddate);
                return (
                  <tr
                    key={applicantId(row, idx)}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="px-4 py-2.5 text-sm font-semibold text-theme-primary whitespace-nowrap">
                      {applicantDisplayName(row)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StageBadge stage={asString(row.pipeline_stage)} />
                    </td>
                    <td
                      className={`px-4 py-2.5 text-sm text-right tabular-nums font-semibold ${daysColor}`}
                    >
                      {days > 0 ? `${days}d` : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-theme-secondary">
                      {asString(row.interest) || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-theme-secondary max-w-[140px] truncate">
                      {asString(row.how_did_you_hear_about_us) || "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <ResultBadge result={asString(row.application_result)} />
                    </td>
                    <td className="px-4 py-2.5 text-xs text-theme-muted tabular-nums whitespace-nowrap">
                      {formatDate(createdRaw)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 pb-4 gap-3">
          <span className="text-xs text-theme-muted tabular-nums">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-xs rounded-lg bg-theme-overlay text-theme-secondary disabled:opacity-30 hover:bg-theme-overlay/80 transition-colors"
            >
              Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
              const pageNum =
                totalPages <= 7
                  ? i
                  : Math.max(0, Math.min(totalPages - 7, page - 3)) + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-7 h-7 text-xs rounded-lg transition-colors ${
                    page === pageNum
                      ? "bg-dcvfd-accent text-white font-bold"
                      : "bg-theme-overlay text-theme-secondary hover:bg-theme-overlay/80"
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-xs rounded-lg bg-theme-overlay text-theme-secondary disabled:opacity-30 hover:bg-theme-overlay/80 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ApplicantTracker() {
  const INTERVAL = 60_000;

  const [dateRange, setDateRange] = useState<DateRange>("all");

  const {
    data: applicantsData,
    isLoading: appLoading,
    lastUpdatedAt: appUpdated,
  } = useApplicants(INTERVAL);
  const { data: aggregatesData, isLoading: aggLoading } =
    useAggregates(INTERVAL);
  const { data: commentsData, isLoading: commentsLoading } =
    useApplicantComments(120_000);

  const applicants = applicantsData?.applicants ?? [];
  const agg = aggregatesData?.aggregates ?? {};

  // ── Date range filter ─────────────────────────────────────────────────────
  const cutoff = useMemo(() => getDateRangeCutoff(dateRange), [dateRange]);

  const rangeFilteredApplicants = useMemo(() => {
    if (!cutoff) return applicants;
    return applicants.filter((a) => {
      const d = applicantCreatedDate(a);
      if (!d) return true; // include if no date — don't hide unknown records
      return d >= cutoff;
    });
  }, [applicants, cutoff]);

  // ── Filter: truly active — no Application_Result, not complete stage, <180 days ──
  const ACTIVE_CUTOFF_DAYS = 180;
  const activeApplicants = useMemo(() => {
    return rangeFilteredApplicants.filter((a) => {
      const stage = asString(a.pipeline_stage);
      // Exclude complete stages
      if (
        stage.includes("8 -") ||
        stage.toLowerCase().includes("complete") ||
        stage.toLowerCase().includes("bod approved") ||
        stage.toLowerCase().includes("board approved")
      ) {
        return false;
      }
      // Exclude applicants with a result (closed)
      const result = asString(
        (a as Record<string, unknown>)["application_result"] ??
          (a as Record<string, unknown>)["Application_Result"],
      );
      if (result && result.trim().length > 0) return false;
      // When no date range filter is applied, limit to <180 days to show current pipeline
      if (!cutoff) {
        const days = asNumber(a.days_since_applied);
        if (days > ACTIVE_CUTOFF_DAYS) return false;
      }
      return true;
    });
  }, [rangeFilteredApplicants, cutoff]);

  // ── KPI: Total in pipeline ────────────────────────────────────────────────
  const totalInPipeline = activeApplicants.length;

  // ── KPI: Disengaged (>30 days without progression) ───────────────────────
  const disengagedCount = useMemo(
    () =>
      activeApplicants.filter((a) => asNumber(a.days_since_applied) > 30)
        .length,
    [activeApplicants],
  );

  // ── KPI: Avg days to BOD ─────────────────────────────────────────────────
  const avgDaysToBod = useMemo(() => {
    const fromAgg = asNumber(agg["avg_days_to_bod"]);
    if (fromAgg > 0) return Math.round(fromAgg);
    // Fallback: compute from applicants with kpi_time_in_process
    const vals = rangeFilteredApplicants
      .map((a) => asNumber(a.kpi_time_in_process))
      .filter((v) => v > 0);
    if (vals.length === 0) return 0;
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  }, [rangeFilteredApplicants, agg]);

  // ── KPI: AIS attendance rate ──────────────────────────────────────────────
  const { aisAttended, aisInvited, aisRate } = useMemo(() => {
    const invited = rangeFilteredApplicants.filter(
      (a) =>
        asString(a.ais_status).toLowerCase().includes("invited") ||
        asString(a.ais_status).toLowerCase().includes("confirmed"),
    ).length;
    const attended = rangeFilteredApplicants.filter(
      (a) =>
        asString(a.ais_status).toLowerCase().includes("attended") ||
        asString(a.ais_status).toLowerCase().includes("completed"),
    ).length;
    const noShow = rangeFilteredApplicants.filter((a) =>
      asString(a.ais_status).toLowerCase().includes("no show"),
    ).length;
    const total = attended + noShow;
    const rate = total > 0 ? Math.round((attended / total) * 100) : null;
    return { aisAttended: attended, aisInvited: invited, aisRate: rate };
  }, [rangeFilteredApplicants]);

  // ── Section 1: Kanban columns ─────────────────────────────────────────────
  const kanbanColumns = useMemo<KanbanColumn[]>(() => {
    const stageMap = new Map<string, KanbanCard[]>();
    for (const stage of PIPELINE_STAGES_ORDERED) {
      stageMap.set(stage, []);
    }

    activeApplicants.forEach((row, idx) => {
      const stage = asString(row.pipeline_stage);
      if (!stageMap.has(stage)) return; // Exclude unmapped or complete

      const card: KanbanCard = {
        id: applicantId(row, idx),
        name: applicantDisplayName(row),
        daysInPipeline: asNumber(row.days_since_applied),
        interest: asString(row.interest) || undefined,
        blockedBy: deriveBlockedBy(row),
      };
      stageMap.get(stage)!.push(card);
    });

    return PIPELINE_STAGES_ORDERED.map((stage) => ({
      stage: stageShortName(stage),
      cards: (stageMap.get(stage) ?? []).sort(
        (a, b) => b.daysInPipeline - a.daysInPipeline,
      ),
    }));
  }, [activeApplicants]);

  // ── Section 2: Disengaged applicants ─────────────────────────────────────
  // These are people who expressed interest and entered the pipeline but have
  // gone silent — they are not "stuck" (that implies a process problem), they
  // are lost contacts who need follow-up outreach.
  const disengagedAlerts = useMemo<AlertItem[]>(() => {
    const alerts: AlertItem[] = [];
    activeApplicants.forEach((row, idx) => {
      const days = asNumber(row.days_since_applied);
      const stage = asString(row.pipeline_stage);
      const name = applicantDisplayName(row);

      // Rule 1: >30 days in pipeline overall — likely disengaged
      const isDisengaged = days > 30;

      // Rule 2: Background stage with no check completed for >14 days
      const isBgPending =
        stage.toLowerCase().includes("background") &&
        !row.screening_one_check_completed &&
        days > 14;

      if (!isDisengaged && !isBgPending) return;

      const severity: AlertItem["severity"] =
        days > 60 ? "critical" : "warning";
      const reason =
        isBgPending && !isDisengaged
          ? "Background check pending (>14 days in pipeline)"
          : days > 60
            ? `${days} days — may need re-engagement outreach`
            : `${days} days — consider follow-up contact`;

      const stageShort = stageShortName(stage) || stage;

      alerts.push({
        id: applicantId(row, idx),
        title: name,
        subtitle: `${stageShort} — ${reason}`,
        severity,
        meta: `${days}d`,
      });
    });
    // Sort: critical first, then by days descending
    return alerts.sort((a, b) => {
      if (a.severity === b.severity)
        return parseInt(b.meta ?? "0") - parseInt(a.meta ?? "0");
      return a.severity === "critical" ? -1 : 1;
    });
  }, [activeApplicants]);

  // ── Section 3: AIS upcoming and no-shows ─────────────────────────────────
  const aisUpcoming = useMemo(
    () =>
      rangeFilteredApplicants.filter((a) => {
        const s = asString(a.ais_status).toLowerCase();
        return s === "invited" || s === "confirmed";
      }),
    [rangeFilteredApplicants],
  );

  const aisNoShows = useMemo(
    () =>
      rangeFilteredApplicants.filter((a) =>
        asString(a.ais_status).toLowerCase().includes("no show"),
      ),
    [rangeFilteredApplicants],
  );

  // ── Section 4: Time-in-process chart data ─────────────────────────────────
  const stageAvgDays = useMemo<StageAvgDays[]>(() => {
    const stageSums = new Map<string, { sum: number; count: number }>();

    for (const stage of PIPELINE_STAGES_ORDERED) {
      stageSums.set(stage, { sum: 0, count: 0 });
    }

    for (const row of rangeFilteredApplicants) {
      const stage = asString(row.pipeline_stage);
      if (!stageSums.has(stage)) continue;
      const days =
        asNumber(row.kpi_time_in_process) || asNumber(row.days_since_applied);
      if (days <= 0) continue;
      const entry = stageSums.get(stage)!;
      entry.sum += days;
      entry.count += 1;
    }

    return PIPELINE_STAGES_ORDERED.map((stage) => {
      const entry = stageSums.get(stage)!;
      return {
        stage: stageShortName(stage),
        avgDays: entry.count > 0 ? Math.round(entry.sum / entry.count) : 0,
        count: entry.count,
      };
    }).filter((d) => d.count > 0);
  }, [rangeFilteredApplicants]);

  // ── Section 5: Coordinator workload ──────────────────────────────────────
  const comments = commentsData?.data ?? [];
  const coordinatorRows = useMemo(
    () => buildCoordinatorWorkload(comments),
    [comments],
  );

  // Detect if author data is actually present
  const hasAuthorData = comments.some(
    (c) => c.author_name || c.author_id || c.author_email,
  );

  // ── Accent colors for KPI cards ───────────────────────────────────────────
  const disengagedAccent =
    disengagedCount > 10 ? "red" : disengagedCount > 5 ? "amber" : "green";
  const bodAccent =
    avgDaysToBod === 0
      ? "neutral"
      : avgDaysToBod <= 50
        ? "green"
        : avgDaysToBod <= 70
          ? "amber"
          : "red";
  const aisAccent =
    aisRate === null
      ? "neutral"
      : aisRate >= 80
        ? "green"
        : aisRate >= 60
          ? "amber"
          : "red";

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Applicant Tracker
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Full pipeline view — kanban board, disengaged applicant follow-up,
            AIS tracker, and time-in-process analysis.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="Live" variant="green" />
          <RefreshDot lastUpdatedAt={appUpdated} />
        </div>
      </div>

      {/* ── Date range toggle ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-medium text-theme-muted uppercase tracking-wider shrink-0">
          Period:
        </span>
        <DateRangeToggle value={dateRange} onChange={setDateRange} />
      </div>

      {/* ── KPI cards ────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Pipeline Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KPICard
            label="Total in Pipeline"
            value={appLoading ? "—" : `${totalInPipeline}`}
            subtitle={`active applicant${totalInPipeline !== 1 ? "s" : ""} (<180d, no result)`}
            accent="neutral"
            isLoading={appLoading}
          />

          <KPICard
            label="Disengaged"
            value={appLoading ? "—" : disengagedCount}
            subtitle={
              disengagedCount === 0
                ? "All applicants progressing"
                : "need follow-up outreach"
            }
            accent={disengagedAccent}
            isLoading={appLoading}
          />

          <KPICard
            label="Avg Days to BOD"
            value={
              aggLoading && appLoading
                ? "—"
                : avgDaysToBod === 0
                  ? "No data"
                  : `${avgDaysToBod}d`
            }
            /* The "45 days" TARGET framing was removed 2026-08-26 (Riley,
               2026-08-25 15:22Z): 45 days is the OLD 2025 objective the 2026 OKRs
               replaced, precisely because it measures funnel volume rather than the
               leak. The MEASUREMENT stays — it is real and useful; only the claim
               that the department has committed to a number is gone. */
            subtitle={
              avgDaysToBod > 0
                ? `${avgDaysToBod}d average · no approved target`
                : "no data"
            }
            accent={bodAccent}
            isLoading={aggLoading && appLoading}
          />

          <KPICard
            label="AIS Attendance"
            value={
              appLoading ? "—" : aisRate !== null ? `${aisRate}%` : "No data"
            }
            subtitle={
              aisRate !== null
                ? `${aisAttended} attended · ${aisInvited} upcoming`
                : "Insufficient AIS records"
            }
            accent={aisAccent}
            isLoading={appLoading}
          />
        </div>
      </section>

      {/* ── Section 1: Pipeline Kanban ────────────────────────────────── */}
      <section>
        <ChartCard
          title="Pipeline Kanban"
          subtitle="Applicants grouped by stage — sorted by days applied, red border = >30 days, amber = 14–30 days"
          headerRight={
            appLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : applicantsData ? (
              <StatusBadge label={`${totalInPipeline} active`} variant="blue" />
            ) : (
              <StatusBadge label="Sync pending" variant="yellow" />
            )
          }
          minHeight={kanbanColumns.length > 0 ? 300 : 160}
        >
          <KanbanBoard columns={kanbanColumns} isLoading={appLoading} />
        </ChartCard>
      </section>

      {/* ── Section 2: Disengaged Applicant Follow-up ────────────────── */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Disengaged alerts — red-bordered, sorted by days desc */}
          <div className="glass rounded-2xl overflow-hidden">
            <div className="px-5 pt-5 pb-3 border-b border-theme-subtle flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-theme-primary">
                  Disengaged Applicants
                </h2>
                <p className="text-xs text-theme-muted mt-0.5">
                  People who need follow-up outreach — in pipeline &gt;30 days
                </p>
              </div>
              {!appLoading && (
                <div className="flex items-center gap-2 shrink-0">
                  {disengagedAlerts.filter((a) => a.severity === "critical")
                    .length > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-500/15 text-status-red">
                      {
                        disengagedAlerts.filter(
                          (a) => a.severity === "critical",
                        ).length
                      }{" "}
                      critical
                    </span>
                  )}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-theme-overlay text-theme-muted">
                    {disengagedAlerts.length}
                  </span>
                </div>
              )}
            </div>
            <div className="p-4">
              {appLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="skeleton h-12 rounded-lg" />
                  ))}
                </div>
              ) : disengagedAlerts.length === 0 ? (
                <div className="py-8 text-center flex flex-col items-center gap-1.5">
                  <span className="text-xl">✓</span>
                  <p className="text-sm font-medium text-status-green">
                    All applicants are actively progressing
                  </p>
                  <p className="text-xs text-theme-muted">
                    No follow-up outreach needed at this time
                  </p>
                </div>
              ) : (
                <AlertList
                  title=""
                  items={disengagedAlerts}
                  emptyMessage="All applicants are actively progressing ✓"
                  isLoading={appLoading}
                  maxVisible={8}
                />
              )}
            </div>
          </div>

          {/* Stage distribution summary */}
          <ChartCard
            title="Stage Distribution"
            subtitle="Count of active applicants per stage"
            minHeight={0}
          >
            <div>
              {appLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-2 py-2.5"
                    >
                      <div className="skeleton h-4 w-32 rounded-md" />
                      <div className="skeleton h-4 w-8 rounded-md" />
                    </div>
                  ))
                : kanbanColumns.map((col) => {
                    const pct =
                      totalInPipeline > 0
                        ? Math.round((col.cards.length / totalInPipeline) * 100)
                        : 0;
                    return (
                      <div
                        key={col.stage}
                        className="flex items-center gap-3 px-2 py-2.5 hover:bg-theme-overlay/50 rounded-lg transition-colors"
                      >
                        <span className="text-sm text-theme-secondary flex-1">
                          {col.stage}
                        </span>
                        {/* Mini bar */}
                        <div className="w-24 bg-theme-subtle rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full bg-dcvfd-accent"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span
                          className={`text-sm font-bold tabular-nums w-6 text-right ${
                            col.cards.length === 0
                              ? "text-theme-muted"
                              : col.cards.length > 5
                                ? "text-status-amber"
                                : "text-theme-primary"
                          }`}
                        >
                          {col.cards.length}
                        </span>
                      </div>
                    );
                  })}
            </div>
          </ChartCard>
        </div>
      </section>

      {/* ── Section 3: AIS Tracker ────────────────────────────────────── */}
      <section>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Upcoming AIS sessions */}
          <ChartCard
            title="AIS — Upcoming Sessions"
            subtitle="Applicants with status Invited or Confirmed"
            headerRight={
              !appLoading && (
                <StatusBadge
                  label={`${aisUpcoming.length} upcoming`}
                  variant={aisUpcoming.length > 0 ? "blue" : "muted"}
                />
              )
            }
            minHeight={0}
          >
            <AisTable
              rows={aisUpcoming}
              emptyMsg="No upcoming AIS sessions — no invited or confirmed applicants"
            />
          </ChartCard>

          {/* No-shows — highlighted in red */}
          <ChartCard
            title="AIS — No-Shows"
            subtitle="Applicants whose AIS status contains No Show — highlighted in red"
            headerRight={
              !appLoading && aisNoShows.length > 0 ? (
                <StatusBadge
                  label={`${aisNoShows.length} no-shows`}
                  variant="red"
                />
              ) : !appLoading ? (
                <StatusBadge label="None" variant="green" />
              ) : null
            }
            minHeight={0}
          >
            <AisTable
              rows={aisNoShows}
              emptyMsg="No AIS no-shows on record"
              highlightNoShow
            />
          </ChartCard>
        </div>
      </section>

      {/* ── AIS Attendance Rate Summary ───────────────────────────────── */}
      {(aisRate !== null || aisAttended > 0) && (
        <section>
          <ChartCard
            title="AIS Attendance Summary"
            subtitle="Overall attendance rate for Applicant Intake Sessions"
            minHeight={0}
          >
            <div className="px-4 py-4 flex flex-wrap gap-6 items-center">
              {/* Rate progress bar */}
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-theme-muted">
                    Attendance Rate
                  </span>
                  <span
                    className="text-sm font-bold tabular-nums"
                    style={{
                      color:
                        aisRate === null
                          ? "#6b7280"
                          : aisRate >= 80
                            ? "#16a34a"
                            : aisRate >= 60
                              ? "#d97706"
                              : "#dc2626",
                    }}
                  >
                    {aisRate !== null ? `${aisRate}%` : "—"}
                  </span>
                </div>
                <div className="w-full bg-theme-subtle rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${aisRate ?? 0}%`,
                      backgroundColor:
                        aisRate === null
                          ? "#6b7280"
                          : aisRate >= 80
                            ? "#16a34a"
                            : aisRate >= 60
                              ? "#d97706"
                              : "#dc2626",
                    }}
                  />
                </div>
                <p className="text-xs text-theme-muted mt-1">Target: 80%</p>
              </div>
              {/* Stat pills */}
              <div className="flex gap-4 flex-wrap">
                <div className="text-center">
                  <p className="text-2xl font-bold tabular-nums text-status-green">
                    {aisAttended}
                  </p>
                  <p className="text-xs text-theme-muted">Attended</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold tabular-nums text-blue-500">
                    {aisInvited}
                  </p>
                  <p className="text-xs text-theme-muted">Upcoming</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold tabular-nums text-status-red">
                    {aisNoShows.length}
                  </p>
                  <p className="text-xs text-theme-muted">No-Shows</p>
                </div>
              </div>
            </div>
          </ChartCard>
        </section>
      )}

      {/* ── Section 4: Time-in-Process Analysis ──────────────────────── */}
      <section>
        <ChartCard
          title="Time-in-Process by Stage"
          subtitle="Average days applicants spend at each pipeline stage — red >30d, amber 14–30d, green <14d"
          headerRight={
            appLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : stageAvgDays.length > 0 ? (
              <StatusBadge
                label={`${stageAvgDays.length} stages`}
                variant="muted"
              />
            ) : null
          }
          minHeight={280}
        >
          <TimeInProcessChart data={stageAvgDays} isLoading={appLoading} />
        </ChartCard>
      </section>

      {/* ── Section 5: Coordinator Workload ──────────────────────────── */}
      <section>
        <ChartCard
          title="Coordinator Workload"
          subtitle={
            hasAuthorData
              ? "Comment activity grouped by author and week — top 20 entries"
              : "Comment activity by author per week — requires author field in comment records"
          }
          headerRight={
            commentsLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : !hasAuthorData ? (
              <StatusBadge label="Future enhancement" variant="muted" />
            ) : (
              <StatusBadge
                label={`${commentsData?.count ?? 0} comments`}
                variant="muted"
              />
            )
          }
          minHeight={0}
        >
          <CoordinatorTable
            rows={coordinatorRows}
            isLoading={commentsLoading}
          />
        </ChartCard>
      </section>

      {/* ── Section 6: Full Applicant List ───────────────────────────── */}
      <section>
        <ChartCard
          title="All Applicants"
          subtitle="Complete applicant list — sortable by any column, filterable by stage, searchable by name"
          headerRight={
            appLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : (
              <StatusBadge
                label={`${rangeFilteredApplicants.length} total`}
                variant="muted"
              />
            )
          }
          minHeight={0}
        >
          <ApplicantDataTable
            rows={rangeFilteredApplicants}
            isLoading={appLoading}
          />
        </ChartCard>
      </section>
    </div>
  );
}
